import { Util } from "@xt/xtbase";

const { Big } = Util;

export function parseSymbolParts(symbol) {
  const s = String(symbol || "").toLowerCase();
  const parts = s.replace(/_cfd$/i, "").split("_").filter(Boolean);
  if (parts.length >= 3 && parts[parts.length - 1] === "cfd") {
    return { base: parts[0], quote: parts[parts.length - 2] };
  }
  return { base: parts[0] || "", quote: parts[1] || "" };
}

const QUOTE_DEDUPE_PRIORITY = ["usdt", "usdc", "usd"];

function quoteDeduplicateRank(quote) {
  const q = String(quote || "").toLowerCase();
  const idx = QUOTE_DEDUPE_PRIORITY.indexOf(q);
  return idx >= 0 ? idx : QUOTE_DEDUPE_PRIORITY.length;
}

/** 同一 base 只保留一条 ticker，优先 USDT > USDC > USD，其次按 24h 成交量 */
export function dedupeTickersByBase(tickers) {
  const bestByBase = new Map();

  for (const ticker of tickers || []) {
    const { base, quote } = parseSymbolParts(ticker.s);
    const baseKey = String(base || "").toLowerCase();
    if (!baseKey) continue;

    const existing = bestByBase.get(baseKey);
    if (!existing) {
      bestByBase.set(baseKey, ticker);
      continue;
    }

    const nextQuoteRank = quoteDeduplicateRank(quote);
    const prevQuoteRank = quoteDeduplicateRank(parseSymbolParts(existing.s).quote);
    if (nextQuoteRank < prevQuoteRank) {
      bestByBase.set(baseKey, ticker);
      continue;
    }
    if (nextQuoteRank > prevQuoteRank) continue;

    const nextVolume = Number(ticker.v ?? 0);
    const prevVolume = Number(existing.v ?? 0);
    if (nextVolume > prevVolume) {
      bestByBase.set(baseKey, ticker);
    }
  }

  return Array.from(bestByBase.values());
}

export function formatPrice(value, buyPoint = 2) {
  const n = Number(value);
  if (!n || Number.isNaN(n)) return "--";
  if (n >= 1000) return Big(n).toFixedCy(buyPoint);
  if (n >= 1) return Big(n).toFixedCy(Math.min(buyPoint, 2));
  return Big(n).toFixedCy(Math.max(buyPoint, 4));
}

export function formatChangeRate(doc) {
  const r = Number(doc?.cr ?? doc?.r ?? 0);
  const pct = r * 100;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${Big(pct).toFixedCy(2)}%`;
}

export function fmtCompact(value) {
  const n = Number(value);
  if (!n || Number.isNaN(n)) return "--";
  if (n >= 1e9) return `${Big(n / 1e9).toFixedMaxCy(2)}B`;
  if (n >= 1e6) return `${Big(n / 1e6).toFixedMaxCy(2)}M`;
  if (n >= 1e3) return `${Big(n / 1e3).toFixedMaxCy(2)}K`;
  return Big(n).toFixedMaxCy(2);
}

export function formatUsdPrice(value, buyPoint = 2) {
  const formatted = formatPrice(value, buyPoint);
  return formatted === "--" ? formatted : `$${formatted}`;
}

export function formatUsdCompact(value) {
  const formatted = fmtCompact(value);
  return formatted === "--" ? formatted : `$${formatted}`;
}

/** ticker24h 暂无 marketCap；优先 get_currencyPage 缓存 map */
export function resolveMarketCap(doc, fund, base, marketCapMap) {
  const key = String(base || "").toLowerCase();
  const fromMap = marketCapMap?.[key];
  if (fromMap != null && Number(fromMap) > 0) return Number(fromMap);

  const fromDoc = Number(doc?.marketCap ?? doc?.cap ?? doc?.mc ?? doc?.marketValue ?? 0);
  if (fromDoc > 0) return fromDoc;
  const currency = fund?.currencyMap?.[key];
  const fromCurrency = Number(currency?.marketCap ?? currency?.marketValue ?? 0);
  return fromCurrency > 0 ? fromCurrency : null;
}

export function formatMarketCap(doc, fund, base, marketCapMap) {
  const cap = resolveMarketCap(doc, fund, base, marketCapMap);
  if (cap == null) return "—";
  return formatUsdCompact(cap);
}

/** 从 ticker 列表计算概览卡片数据 */
export function computeHighlights(tickers, marketConfig) {
  const valid = (tickers || []).filter((t) => {
    const cfg = marketConfig?.[t.s];
    return cfg && cfg.state !== "DELISTED" && String(t.s || "").toLowerCase().endsWith("_cfd");
  });
  const deduped = dedupeTickersByBase(valid);

  const byChange = [...deduped].sort(
    (a, b) => Number(b.cr ?? b.r ?? 0) - Number(a.cr ?? a.r ?? 0)
  );
  const byVolume = [...deduped].sort((a, b) => Number(b.v ?? 0) - Number(a.v ?? 0));
  const byWeight = [...deduped].sort((a, b) => {
    const w1 = Number(marketConfig?.[a.s]?.displayWeight ?? 0);
    const w2 = Number(marketConfig?.[b.s]?.displayWeight ?? 0);
    return w2 - w1;
  });

  return {
    gainers: byChange.slice(0, 3),
    hot: byVolume.slice(0, 3),
    fresh: byWeight.slice(0, 3),
    volume: byVolume.slice(0, 3),
  };
}

export function sortKeyToMarketList(sort) {
  if (!sort?.key) return "";
  const dir = sort.dir === "asc" ? "up" : "down";
  const map = { name: "name", price: "price", change24h: "rate", volume: "volume" };
  const attr = map[sort.key];
  return attr ? `${attr}_${dir}` : "";
}

export function marketListSortToKey(sortBy) {
  if (!sortBy) return { key: "volume", dir: "desc" };
  const [attr, dir] = sortBy.split("_");
  const map = { name: "name", price: "price", rate: "change24h", volume: "volume" };
  return { key: map[attr] || "volume", dir: dir === "up" ? "asc" : "desc" };
}
